import { useEffect, useState, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabase';
import type { Employee } from '../types';

// Types de notifications supportées
export type NotificationType = 'material' | 'leave' | 'timesheet';

export interface Notification {
  id: string;
  type: NotificationType;
  title: string;
  message: string;
  timestamp: Date;
  read: boolean;
  data?: Record<string, unknown>;
}

interface NotificationGroup {
  type: NotificationType;
  label: string;
  icon: string;
  color: string;
  count: number;
  notifications: Notification[];
}

// Configuration des types de notifications
const NOTIFICATION_CONFIG: Record<NotificationType, { label: string; icon: string; color: string }> = {
  material: { label: 'Matériel', icon: '📦', color: '#f97316' },
  leave: { label: 'Congés', icon: '🏖️', color: '#10b981' },
  timesheet: { label: 'Feuilles d\'heures', icon: '⏱️', color: '#3b82f6' }
};

// Son de notification synthétisé via Web Audio API
function playNotificationSound() {
  try {
    const audioContext = new (window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)();

    // Créer un oscillateur pour le son principal
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);

    // Configuration du son (ton doux et agréable)
    oscillator.frequency.setValueAtTime(880, audioContext.currentTime); // Note A5
    oscillator.frequency.setValueAtTime(1100, audioContext.currentTime + 0.1); // Monte légèrement
    oscillator.type = 'sine';

    // Volume avec fade out pour un son non-agressif
    gainNode.gain.setValueAtTime(0.15, audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);

    // Jouer le son
    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + 0.3);

    // Nettoyer après utilisation
    setTimeout(() => {
      audioContext.close();
    }, 500);
  } catch (error) {
    console.warn('Impossible de jouer le son de notification:', error);
  }
}

// Vérifier la permission initiale (appelé une seule fois)
function getInitialPermissionState(): boolean {
  if (typeof window === 'undefined') return false;
  if (!('Notification' in window)) return false;
  return Notification.permission === 'granted';
}

export function useNotifications(user: Employee | null) {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [permissionGranted, setPermissionGranted] = useState(getInitialPermissionState);
  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);
  const swRegistrationRef = useRef<ServiceWorkerRegistration | null>(null);

  // Enregistrer le Service Worker au montage
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/sw.js')
        .then((registration) => {
          console.log('[SW] Enregistré avec succès, scope:', registration.scope);
          swRegistrationRef.current = registration;
        })
        .catch((err) => {
          console.warn('[SW] Échec enregistrement:', err);
        });
    } else {
      console.warn('[SW] Service Workers non supportés dans ce navigateur');
    }
  }, []);

  // Fermer les notifications bureau quand l'utilisateur revient sur l'onglet
  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (!document.hidden) {
        // L'utilisateur est revenu sur l'onglet → fermer toutes les notifications bureau
        try {
          const registration = swRegistrationRef.current || await navigator.serviceWorker?.ready;
          if (registration) {
            const activeNotifs = await registration.getNotifications();
            console.log('[Notif Desktop] Fermeture de', activeNotifs.length, 'notification(s) au retour sur l\'onglet');
            activeNotifs.forEach(n => n.close());
          }
        } catch (err) {
          console.warn('[Notif Desktop] Erreur fermeture notifications:', err);
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  // Demander la permission pour les notifications desktop
  const requestPermission = useCallback(async () => {
    if (!('Notification' in window)) {
      console.warn('Ce navigateur ne supporte pas les notifications desktop');
      return false;
    }

    if (Notification.permission === 'granted') {
      setPermissionGranted(true);
      return true;
    }

    if (Notification.permission !== 'denied') {
      const permission = await Notification.requestPermission();
      const granted = permission === 'granted';
      setPermissionGranted(granted);
      return granted;
    }

    return false;
  }, []);

  // Afficher une notification desktop via Service Worker (plus robuste que new Notification())
  const showDesktopNotification = useCallback(async (title: string, body: string, icon?: string) => {
    console.log('[Notif Desktop] Tentative:', {
      permissionGranted,
      documentHidden: document.hidden,
      notificationSupported: 'Notification' in window,
      permission: typeof Notification !== 'undefined' ? Notification.permission : 'N/A',
      swRegistration: !!swRegistrationRef.current
    });

    if (!permissionGranted) {
      console.log('[Notif Desktop] Permission non accordée, notification ignorée');
      return;
    }

    try {
      // Méthode 1 : Via Service Worker (recommandée, la plus robuste)
      const registration = swRegistrationRef.current || await navigator.serviceWorker?.ready;
      if (registration) {
        await registration.showNotification(title, {
          body,
          icon: icon || '/logo.svg',
          tag: `centralis-${Date.now()}`,
          requireInteraction: true,
          silent: false
        });
        console.log('[Notif Desktop] Notification envoyée via Service Worker');
        return;
      }

      // Méthode 2 : Fallback classique (si SW pas disponible)
      console.log('[Notif Desktop] SW non disponible, fallback sur new Notification()');
      new globalThis.Notification(title, {
        body,
        icon: icon || '/logo.svg',
        tag: `centralis-${Date.now()}`,
        requireInteraction: true
      });
      console.log('[Notif Desktop] Notification envoyée via Notification API classique');
    } catch (err) {
      console.warn('[Notif Desktop] Erreur envoi notification:', err);
    }
  }, [permissionGranted]);

  // Ajouter une notification
  const addNotification = useCallback((type: NotificationType, title: string, message: string, data?: Record<string, unknown>) => {
    const notification: Notification = {
      id: `${type}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type,
      title,
      message,
      timestamp: new Date(),
      read: false,
      data
    };

    setNotifications(prev => [notification, ...prev].slice(0, 50)); // Garder max 50 notifications

    // Jouer le son de notification
    playNotificationSound();

    // Notification desktop groupée par type
    const config = NOTIFICATION_CONFIG[type];
    showDesktopNotification(`${config.icon} ${config.label}`, message);
  }, [showDesktopNotification]);

  // Marquer comme lu
  const markAsRead = useCallback((id: string) => {
    setNotifications(prev =>
      prev.map(n => n.id === id ? { ...n, read: true } : n)
    );
  }, []);

  // Marquer tout comme lu par type
  const markAllAsReadByType = useCallback((type: NotificationType) => {
    setNotifications(prev =>
      prev.map(n => n.type === type ? { ...n, read: true } : n)
    );
  }, []);

  // Effacer les notifications lues
  const clearRead = useCallback(() => {
    setNotifications(prev => prev.filter(n => !n.read));
  }, []);

  // Grouper les notifications par type
  const groupedNotifications: NotificationGroup[] = Object.entries(NOTIFICATION_CONFIG).map(([type, config]) => {
    const typeNotifications = notifications.filter(n => n.type === type as NotificationType);
    return {
      type: type as NotificationType,
      ...config,
      count: typeNotifications.filter(n => !n.read).length,
      notifications: typeNotifications
    };
  }).filter(group => group.notifications.length > 0);

  // Nombre total de notifications non lues
  const unreadCount = notifications.filter(n => !n.read).length;

  // Vérifier si l'utilisateur a le droit de recevoir des notifications
  const isAdmin = user?.role === 'admin';
  const isBE = user?.role === 'bureau_etude';
  const canReceiveNotifications = isAdmin || isBE;

  // Configurer les abonnements Supabase Realtime
  useEffect(() => {
    if (!user) return;
    if (!canReceiveNotifications) return;

    // Créer un channel unique pour cet utilisateur
    const channel = supabase.channel(`notifications-${user.id}`);
    channelRef.current = channel;

    // Écouter les nouvelles demandes de matériel
    channel.on(
      'postgres_changes',
      {
        event: 'INSERT',
        schema: 'public',
        table: 'material_requests',
        filter: 'status=eq.pending'
      },
      async (payload) => {
        const request = payload.new as { id: string; description: string; urgency: string; target: string; employee_id: string; project_id?: string };

        // Admin reçoit les demandes 'direction', BE reçoit les demandes 'be'
        if ((isAdmin && request.target === 'direction') || (isBE && request.target === 'be')) {
          // Récupérer le nom de l'employé
          const { data: employee } = await supabase
            .from('employees')
            .select('full_name')
            .eq('id', request.employee_id)
            .single();

          const employeeName = employee?.full_name || 'Un employé';
          const urgencyLabel = request.urgency === 'urgent' ? '🔴 URGENT' : '';

          addNotification(
            'material',
            'Nouvelle demande de matériel',
            `${urgencyLabel} ${employeeName} a fait une demande : ${request.description.substring(0, 50)}${request.description.length > 50 ? '...' : ''}`,
            { requestId: request.id }
          );
        }
      }
    );

    // Écouter les nouvelles demandes de congés (admin uniquement)
    if (isAdmin) {
      channel.on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'leaves',
          filter: 'status=eq.pending'
        },
        async (payload) => {
          const leave = payload.new as { id: string; type: string; start_date: string; end_date: string; employee_id: string };

          // Récupérer le nom de l'employé
          const { data: employee } = await supabase
            .from('employees')
            .select('full_name')
            .eq('id', leave.employee_id)
            .single();

          const employeeName = employee?.full_name || 'Un employé';
          const typeLabels: Record<string, string> = {
            cp: 'Congés payés',
            maladie: 'Arrêt maladie',
            sans_solde: 'Sans solde',
            formation: 'Formation',
            autre: 'Autre absence'
          };
          const typeLabel = typeLabels[leave.type] || leave.type;

          addNotification(
            'leave',
            'Nouvelle demande de congé',
            `${employeeName} demande : ${typeLabel} du ${new Date(leave.start_date).toLocaleDateString('fr-FR')} au ${new Date(leave.end_date).toLocaleDateString('fr-FR')}`,
            { leaveId: leave.id }
          );
        }
      );

      // Écouter les feuilles d'heures soumises pour validation
      channel.on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'weekly_sheets',
          filter: 'status=eq.pending'
        },
        async (payload) => {
          const sheet = payload.new as { id: string; employee_id: string; week_number: number; year: number; status: string };
          const oldSheet = payload.old as { status: string };

          // Ne notifier que si le statut passe à 'pending' (nouvelle soumission)
          // Guard: oldSheet.status peut être undefined si REPLICA IDENTITY n'est pas FULL
          if (oldSheet.status && oldSheet.status !== 'pending' && sheet.status === 'pending') {
            // Récupérer le nom de l'employé
            const { data: employee } = await supabase
              .from('employees')
              .select('full_name')
              .eq('id', sheet.employee_id)
              .single();

            const employeeName = employee?.full_name || 'Un employé';

            addNotification(
              'timesheet',
              'Feuille d\'heures à valider',
              `${employeeName} a soumis sa feuille S${sheet.week_number} ${sheet.year}`,
              { sheetId: sheet.id }
            );
          }
        }
      );
    }

    // S'abonner au channel
    channel.subscribe((status) => {
      if (status === 'SUBSCRIBED') {
        console.log('Notifications Realtime connectées');
      }
    });

    // Cleanup
    return () => {
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
    };
  }, [user, isAdmin, isBE, canReceiveNotifications, addNotification]);

  return {
    notifications,
    groupedNotifications,
    unreadCount,
    permissionGranted,
    requestPermission,
    addNotification,
    markAsRead,
    markAllAsReadByType,
    clearRead
  };
}
